import { apiClient } from './apiClient';

/**
 * Adapter implementing IDataAgentSessionService from @medlink/dataAgent-frontend-core
 */
class DataAgentSessionServiceAdapter {
  constructor() {
    this.BASE_URL = '/dataAgent/sessions';
  }

  /**
   * Get all sessions for the current user
   * @param {Object} options - Options for the request
   * @returns {Promise<Array>} Array of sessions
   */
  async getSessions(options = {}) {
    const { limit = 50, offset = 0 } = options;
    const url = `${this.BASE_URL}?limit=${limit}&offset=${offset}`;
    const response = await apiClient.get(url);
    return response.data || [];
  }

  /**
   * Get a session by ID
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object|null>} Session object or null
   */
  async getSessionById(sessionId) {
    const response = await apiClient.get(`${this.BASE_URL}/${sessionId}`);
    return response.data || null;
  }

  /**
   * Create a new session
   * @param {Object} data - Session data
   * @returns {Promise<Object>} Created session
   */
  async createSession(data) {
    const response = await apiClient.post(this.BASE_URL, {
      session: data
    });
    return response.data;
  }

  /**
   * Update a session
   * @param {string} sessionId - Session ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated session
   */
  async updateSession(sessionId, updates) {
    const response = await apiClient.put(`${this.BASE_URL}/${sessionId}`, {
      updates
    });
    return response.data;
  }

  /**
   * Delete a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<void>}
   */
  async deleteSession(sessionId) {
    await apiClient.delete(`${this.BASE_URL}/${sessionId}`);
  }

  /**
   * Get conversation history for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Array>} Array of messages
   */
  async getConversationHistory(sessionId) {
    const url = `${this.BASE_URL}/${sessionId}/history`;
    const response = await apiClient.get(url);
    const history = response.data || response || [];
    let lastUserQuery = '';

    // Map to DataAgentMessage format
    return history.map(msg => {
      if (msg.role === 'user') {
        lastUserQuery = msg.content || '';
      }

      return {
        id: msg.id || msg.requestId || msg.resultId || `msg_${msg.sequence}_${Date.now()}`,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        sources: msg.sources,
        visualizations: msg.visualizations,
        feedback: msg.feedback,
        planId: msg.planId || null,
        data: msg.data || null,
        needsClarification: Boolean(msg?.data?.needsClarification),
        clarificationType: msg?.data?.clarificationType || null,
        ambiguities: Array.isArray(msg?.data?.ambiguities) ? msg.data.ambiguities : [],
        originalQuery: msg.role === 'assistant' ? lastUserQuery : undefined
      };
    });
  }
}

/**
 * Legacy DataAgentSessionService - maintains backward compatibility
 */
export class DataAgentSessionService extends DataAgentSessionServiceAdapter {
  async getSessionsREST(options = {}) {
    const { limit = 50, offset = 0 } = options;
    const url = `${this.BASE_URL}?limit=${limit}&offset=${offset}`;
    return apiClient.get(url);
  }

  async getSessionByIdREST(sessionId) {
    return apiClient.get(`${this.BASE_URL}/${sessionId}`);
  }

  async createSessionREST(sessionData) {
    try {
      const response = await apiClient.post('/dataAgent/sessions', {
        session: sessionData
      });
      return response;
    } catch (error) {
      console.error("Error creating dataAgent session:", error);
      return {
        code: error.response?.status || 500,
        message: error.message || "Error creating dataAgent session"
      };
    }
  }

  async updateSessionREST(sessionId, updates) {
    try {
      const response = await apiClient.put(`${this.BASE_URL}/${sessionId}`, {
        updates: updates
      });
      return response;
    } catch (error) {
      console.error(`Error updating session ${sessionId}:`, error);
      return {
        code: error.response?.status || 500,
        message: error.message || "Error updating dataAgent session"
      };
    }
  }

  async addMessageREST(sessionId, message) {
    try {
      const response = await apiClient.post(`${this.BASE_URL}/${sessionId}/messages`, {
        message: message
      });
      return response.data ?? response;
    } catch (error) {
      console.error(`Error adding message to session ${sessionId}:`, error);
      return {
        code: error.response?.status || 500,
        message: error.message || "Error adding message to dataAgent session"
      };
    }
  }

  async updateMessageFeedbackREST(sessionId, messageId, feedback) {
    return apiClient.post(`/dataAgent/sessions/${sessionId}/messages/${messageId}/feedback`, feedback);
  }

  async updateVisualizationsREST(sessionId, messageIndex, visualizations) {
    try {
      const response = await apiClient.post(
        `${this.BASE_URL}/${sessionId}/messages/${messageIndex}/visualizations`,
        { visualizations }
      );
      return response;
    } catch (error) {
      console.error(`Error updating visualizations for session ${sessionId}:`, error);
      return {
        code: error.response?.status || 500,
        message: error.message || "Error updating visualizations"
      };
    }
  }

  async deleteSessionREST(sessionId) {
    return apiClient.delete(`${this.BASE_URL}/${sessionId}`);
  }

  // This method is needed for backward compat with DataAgentSidePanel
  async getConversationHistory(sessionId) {
    const url = `/dataAgent/sessions/${sessionId}/history`;
    return apiClient.get(url);
  }
}

// Singleton instances
export const dataAgentSessionService = new DataAgentSessionService();
export const dataAgentSessionAdapter = new DataAgentSessionServiceAdapter();
